import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Anchor, Bolt, Calendar, Heart, Info, Star } from 'lucide-react';
import React from 'react';
import { CotaNauticaModal } from './CotaNauticaModal';

export interface BoatOfferCardProps {
    id: number;
    title: string;
    location: string;
    price: string;
    year: string;
    engine_quantity: number;
    engine_model?: string;
    condition: string;
    imageUrl?: string;
    isFavorited?: boolean;
    onFavorite?: () => void;
    onViewOffer?: () => void;
    isHighlighted: boolean;
    isCotaNautica: boolean;
    className?: string;
}

export const BoatOfferCard: React.FC<BoatOfferCardProps> = ({
    id: _id,
    title,
    location,
    price,
    year,
    engine_quantity,
    engine_model,
    condition,
    imageUrl,
    isFavorited = false,
    onFavorite,
    onViewOffer,
    isHighlighted = false,
    isCotaNautica = false,
    className = '',
}) => {
    const [isCotaNauticaModalOpen, setIsCotaNauticaModalOpen] = React.useState(false);

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFavorite?.();
    };

    const handleViewOfferClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onViewOffer?.();
    };

    const handleClickCotaNauticaInfo = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsCotaNauticaModalOpen(true);
    };

    return (
        <Card className={`h-[28rem] gap-3 overflow-hidden rounded-xl py-0 sm:h-[25rem] ${className}`}>
            <div className="relative h-1/2 bg-gray-200">
                <img
                    src={imageUrl}
                    alt={title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full cursor-pointer object-cover"
                    onClick={handleViewOfferClick}
                />
                <Button variant="ghost" size="sm" className="absolute top-2 right-2 z-10 bg-white/80 hover:bg-white" onClick={handleFavoriteClick}>
                    <Heart className={`h-4 w-4 ${isFavorited ? 'fill-red-500 stroke-red-500' : ''}`} />
                </Button>
                {isHighlighted && (
                    <div className="absolute top-2 left-2 z-10 bg-green-600 flex gap-1 items-center py-1 px-2 rounded-md text-white" onClick={handleFavoriteClick}>
                        <Star className="h-4 w-4 fill-white" /> <span className='uppercase text-sm'>Oferta Destaque</span>
                    </div>
                )}
            </div>
            <CardContent className="flex h-1/2 flex-col justify-start p-0">
                <div className="px-4">
                    <h3 className={`mb-1 text-sm font-semibold sm:text-base ${isCotaNautica ? 'line-clamp-1' : ''}`}>{title}</h3>
                    <div className="flex flex-row items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-2.5 w-2.5 sm:h-3 sm:w-3">
                            <path
                                d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"
                                fill="black"
                                stroke="black"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <circle cx="12" cy="10" r="5" fill="white" stroke="black" strokeWidth="2" />
                        </svg>
                        <p className="ml-1 text-xs text-gray-600 sm:text-sm">{location}</p>
                    </div>
                </div>
                <div className="mt-auto">
                    <div className="px-4">
                        <p className="mb-2 text-base font-bold text-black sm:text-lg">{price}</p>
                        <div className="flex flex-row items-center justify-between pb-2">
                            <div className="flex flex-row">
                                <div className="flex flex-row items-center">
                                    <Calendar className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                                    <p className="ml-1 text-xs font-semibold text-gray-500">{year}</p>
                                </div>
                                {engine_quantity > 0 && (
                                    <div className='flex flex-row gap-2 ml-2'>
                                        <span className='border-l'></span>
                                        <div className="flex flex-row items-center">
                                            <Bolt className="h-2 w-2 sm:h-2.5 sm:w-2.5" />
                                            <p className="ml-1 text-xs font-semibold text-gray-500">{engine_quantity}x {engine_model}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Badge className="rounded-lg border border-blue-800 bg-white text-xs font-bold text-blue-800">{condition}</Badge>
                        </div>
                    </div>
                    {isCotaNautica && (
                        <div onClick={handleClickCotaNauticaInfo} className='flex justify-between p-2 items-center bg-blue-500/20 hover:bg-blue-500/50 text-blue-800 m-2 mt-0 rounded-md hover:cursor-pointer'>
                            <p className='flex items-center gap-2 text-sm'>
                                <Anchor className="h-4 w-4" /> Disponível via Cota Náutica
                            </p>
                            <Info className="h-4 w-4" />
                        </div>
                    )}
                    <Button className="w-full rounded-t-none bg-blue-800 text-sm hover:bg-blue-700 sm:textbg--base" onClick={handleViewOfferClick}>
                        Ver oferta
                    </Button>
                </div>
            </CardContent>

            <CotaNauticaModal
                isOpen={isCotaNauticaModalOpen}
                onClose={() => setIsCotaNauticaModalOpen(false)}
            />
        </Card>
    );
};
